/**
 * Pure builders for the HealthKit → `/health-observations` upload payload.
 *
 * Kept free of react-native imports so node-based contract tests can exercise
 * the exact payload the app uploads — the dashboard API only derives canonical
 * fertility signals when the payload's zone evidence is present and
 * self-consistent (see `observationCaptureZone.ts`).
 */
import type {
  CategoryTypeIdentifier,
  QuantityTypeIdentifier,
} from "@kingstinct/react-native-healthkit";
import { resolveObservedCaptureZone } from "@src/packages/health/observationCaptureZone";
import type { paths } from "@src/packages/openapi/schema";

type HealthObservationPostBody = NonNullable<
  paths["/patients/{patientId}/health-observations"]["post"]["requestBody"]
>["content"]["application/json"];
export type HealthObservationInput =
  HealthObservationPostBody["observations"][number];
export type HealthObservationCategory = HealthObservationInput["category"];

/**
 * The category-value enums the payload builders read, typed structurally so
 * the real @kingstinct module and plain test stubs both satisfy them without
 * loading the native runtime.
 */
export type HealthKitCategoryValueEnums = {
  CategoryValueMenstrualFlow: Record<
    "unspecified" | "light" | "medium" | "heavy" | "none",
    number
  >;
  CategoryValueCervicalMucusQuality: Record<
    "dry" | "sticky" | "creamy" | "watery" | "eggWhite",
    number
  >;
  CategoryValueOvulationTestResult: Record<
    "negative" | "positive" | "indeterminate" | "estrogenSurge",
    number
  >;
  CategoryValuePregnancyTestResult: Record<
    "negative" | "positive" | "indeterminate",
    number
  >;
  CategoryValueProgesteroneTestResult: Record<
    "negative" | "positive" | "indeterminate",
    number
  >;
  CategoryValueContraceptive: Record<
    | "unspecified"
    | "implant"
    | "injection"
    | "intrauterineDevice"
    | "intravaginalRing"
    | "oral"
    | "patch",
    number
  >;
  CategoryValueSleepAnalysis: Record<
    "inBed" | "asleep" | "awake" | "asleepCore" | "asleepDeep" | "asleepREM",
    number
  >;
  CategoryValueSeverity: Record<
    "unspecified" | "notPresent" | "mild" | "moderate" | "severe",
    number
  >;
  CategoryValuePresence: Record<"present" | "notPresent", number>;
};

/**
 * The sample fields the upload payload reads, typed structurally so real
 * HealthKit samples and node-test fixtures are both accepted.
 */
type HealthKitSampleLike = {
  uuid: string;
  startDate: Date;
  endDate: Date;
  hasUndeterminedDuration?: boolean;
  metadata?: unknown;
  sourceRevision?: {
    source?: {
      name?: string | null;
      bundleIdentifier?: string | null;
    } | null;
    version?: string | null;
    productType?: string | null;
  } | null;
  device?: {
    name?: string | null;
    model?: string | null;
    manufacturer?: string | null;
    localIdentifier?: string | null;
  } | null;
};

export type CategorySampleForUpload = HealthKitSampleLike & {
  categoryType: string;
  value: number;
};

export type QuantitySampleForUpload = HealthKitSampleLike & {
  quantityType: string;
  unit: string;
  quantity: number;
};

export type CategoryConfig = {
  identifier: CategoryTypeIdentifier;
  category: HealthObservationCategory;
  label: string;
};

export type QuantityConfig = {
  identifier: QuantityTypeIdentifier;
  category: HealthObservationCategory;
  label: string;
  unit: string;
  limit?: number;
};

export const EXTERNAL_SOURCE = "apple-health";

export function categoryValueLabel(
  identifier: CategoryTypeIdentifier,
  value: number,
  healthkit: HealthKitCategoryValueEnums,
): string {
  const maps: Partial<Record<CategoryTypeIdentifier, Record<number, string>>> =
    {
      HKCategoryTypeIdentifierMenstrualFlow: {
        [healthkit.CategoryValueMenstrualFlow.unspecified]: "unspecified",
        [healthkit.CategoryValueMenstrualFlow.light]: "light",
        [healthkit.CategoryValueMenstrualFlow.medium]: "medium",
        [healthkit.CategoryValueMenstrualFlow.heavy]: "heavy",
        [healthkit.CategoryValueMenstrualFlow.none]: "none",
      },
      HKCategoryTypeIdentifierCervicalMucusQuality: {
        [healthkit.CategoryValueCervicalMucusQuality.dry]: "dry",
        [healthkit.CategoryValueCervicalMucusQuality.sticky]: "sticky",
        [healthkit.CategoryValueCervicalMucusQuality.creamy]: "creamy",
        [healthkit.CategoryValueCervicalMucusQuality.watery]: "watery",
        [healthkit.CategoryValueCervicalMucusQuality.eggWhite]: "eggWhite",
      },
      HKCategoryTypeIdentifierOvulationTestResult: {
        [healthkit.CategoryValueOvulationTestResult.negative]: "negative",
        [healthkit.CategoryValueOvulationTestResult.positive]: "positive",
        [healthkit.CategoryValueOvulationTestResult.indeterminate]:
          "indeterminate",
        [healthkit.CategoryValueOvulationTestResult.estrogenSurge]:
          "estrogenSurge",
      },
      HKCategoryTypeIdentifierPregnancyTestResult: {
        [healthkit.CategoryValuePregnancyTestResult.negative]: "negative",
        [healthkit.CategoryValuePregnancyTestResult.positive]: "positive",
        [healthkit.CategoryValuePregnancyTestResult.indeterminate]:
          "indeterminate",
      },
      HKCategoryTypeIdentifierProgesteroneTestResult: {
        [healthkit.CategoryValueProgesteroneTestResult.negative]: "negative",
        [healthkit.CategoryValueProgesteroneTestResult.positive]: "positive",
        [healthkit.CategoryValueProgesteroneTestResult.indeterminate]:
          "indeterminate",
      },
      HKCategoryTypeIdentifierContraceptive: {
        [healthkit.CategoryValueContraceptive.unspecified]: "unspecified",
        [healthkit.CategoryValueContraceptive.implant]: "implant",
        [healthkit.CategoryValueContraceptive.injection]: "injection",
        [healthkit.CategoryValueContraceptive.intrauterineDevice]:
          "intrauterineDevice",
        [healthkit.CategoryValueContraceptive.intravaginalRing]:
          "intravaginalRing",
        [healthkit.CategoryValueContraceptive.oral]: "oral",
        [healthkit.CategoryValueContraceptive.patch]: "patch",
      },
      HKCategoryTypeIdentifierSleepAnalysis: {
        [healthkit.CategoryValueSleepAnalysis.inBed]: "inBed",
        [healthkit.CategoryValueSleepAnalysis.asleep]: "asleep",
        [healthkit.CategoryValueSleepAnalysis.awake]: "awake",
        [healthkit.CategoryValueSleepAnalysis.asleepCore]: "asleepCore",
        [healthkit.CategoryValueSleepAnalysis.asleepDeep]: "asleepDeep",
        [healthkit.CategoryValueSleepAnalysis.asleepREM]: "asleepREM",
      },
    };

  const severity = healthkit.CategoryValueSeverity;
  const presence = healthkit.CategoryValuePresence;
  if (Object.prototype.hasOwnProperty.call(maps, identifier)) {
    return maps[identifier]?.[value] ?? String(value);
  }
  if (value >= severity.unspecified && value <= severity.severe) {
    return (
      {
        [severity.unspecified]: "unspecified",
        [severity.notPresent]: "notPresent",
        [severity.mild]: "mild",
        [severity.moderate]: "moderate",
        [severity.severe]: "severe",
      }[value] ?? String(value)
    );
  }
  if (value === presence.present) return "present";
  if (value === presence.notPresent) return "notPresent";
  return String(value);
}

function sourceDetails(sample: HealthKitSampleLike) {
  return {
    sourceName: sample.sourceRevision?.source?.name ?? null,
    sourceBundleIdentifier:
      sample.sourceRevision?.source?.bundleIdentifier ?? null,
    sourceVersion: sample.sourceRevision?.version ?? null,
    sourceProductType: sample.sourceRevision?.productType ?? null,
    deviceName: sample.device?.name ?? null,
    deviceModel: sample.device?.model ?? null,
    deviceManufacturer: sample.device?.manufacturer ?? null,
  };
}

export function categoryObservation(
  sample: CategorySampleForUpload,
  config: CategoryConfig,
  healthkit: HealthKitCategoryValueEnums,
  deviceTimeZone: string,
): HealthObservationInput {
  const details = sourceDetails(sample);
  const captureZone = resolveObservedCaptureZone({
    startDate: sample.startDate,
    metadata: sample.metadata,
    fallbackTimeZone: deviceTimeZone,
  });
  return {
    externalSource: EXTERNAL_SOURCE,
    sourceSampleId: sample.uuid,
    sourceBundleIdentifier: details.sourceBundleIdentifier,
    sourceDeviceId: sample.device?.localIdentifier ?? null,
    category: config.category,
    type: config.identifier,
    valueType: "category",
    valueNumeric: sample.value,
    valueText: categoryValueLabel(config.identifier, sample.value, healthkit),
    startAtIso: sample.startDate.toISOString(),
    endAtIso: sample.endDate.toISOString(),
    localDate: captureZone.localDate,
    observedTimeZone: captureZone.observedTimeZone,
    observedUtcOffset: captureZone.observedUtcOffset,
    notes: config.label,
    structured: {
      ...details,
      healthKitValue: sample.value,
      hasUndeterminedDuration: sample.hasUndeterminedDuration ?? false,
      metadata: sample.metadata ?? {},
    },
    raw: {
      uuid: sample.uuid,
      categoryType: sample.categoryType,
    },
  };
}

export function quantityObservation(
  sample: QuantitySampleForUpload,
  config: QuantityConfig,
  deviceTimeZone: string,
): HealthObservationInput {
  const details = sourceDetails(sample);
  const captureZone = resolveObservedCaptureZone({
    startDate: sample.startDate,
    metadata: sample.metadata,
    fallbackTimeZone: deviceTimeZone,
  });
  return {
    externalSource: EXTERNAL_SOURCE,
    sourceSampleId: sample.uuid,
    sourceBundleIdentifier: details.sourceBundleIdentifier,
    sourceDeviceId: sample.device?.localIdentifier ?? null,
    category: config.category,
    type: config.identifier,
    valueType: "quantity",
    valueNumeric: sample.quantity,
    unit: sample.unit,
    startAtIso: sample.startDate.toISOString(),
    endAtIso: sample.endDate.toISOString(),
    localDate: captureZone.localDate,
    observedTimeZone: captureZone.observedTimeZone,
    observedUtcOffset: captureZone.observedUtcOffset,
    notes: config.label,
    structured: {
      ...details,
      requestedUnit: config.unit,
      hasUndeterminedDuration: sample.hasUndeterminedDuration ?? false,
      metadata: sample.metadata ?? {},
    },
    raw: {
      uuid: sample.uuid,
      quantityType: sample.quantityType,
    },
  };
}
